import { Component, h, hydrate, render, VNode, Fragment } from "preact";
import ssrPrepass from "preact-ssr-prepass";

import { Router } from "./router/router";
import "./global.scss";
import { AssetManager } from "../asset_manager/asset_manager";
import { GlobalFlags } from "../flags/global_flags";
import { AppContext } from "./app_context";
import { assert } from "../utils/asserts";
import { routes } from "routes:./routes.yaml";
import { getRoute, LoadedRoute, loadRoute, RouterComponent } from "./router_component";
import renderToString from "preact-render-to-string";
import { DataContext } from "../data/react_context";
import { ClientDataProvider } from "../data/client_data_provider";
import { ServerDataProvider } from "../data/server_data_provider";
import { dataSpec } from "./data/data";
import { DataProvider } from "../data/data_provider";
import { DataCacheObject } from "../data/data_cache_object";
import { ErrorWrapper } from "./error_wrapper";
import { AppErrorType } from "./app_error";

interface InitParams {
  flags: GlobalFlags;
  dataCacheObject?: DataCacheObject;
}

export class App {
  readonly router = new Router(routes);

  private readonly assetManager: AssetManager;
  private readonly appContext: AppContext;
  private readonly dataCacheObject: DataCacheObject | undefined;

  private lastReportedAppErrorType: AppErrorType | null = null;

  constructor({ flags, dataCacheObject }: InitParams) {
    this.assetManager = new AssetManager(flags.cdnBaseUrl);
    this.appContext = {
      assetManager: this.assetManager,
      navigate: (path: string) => {},
      reportAppError: (errorType: AppErrorType) => {
        this.lastReportedAppErrorType = errorType;
      },
    };

    this.dataCacheObject = dataCacheObject;
  }

  async hydrate() {
    assert(BUILD_PLATFORM === "browser");

    const route = getRoute(this.router);
    const loadedRoute = await loadRoute(route);
    const dataProvider = DEV_MODE
      ? new ServerDataProvider(dataSpec)
      : new ClientDataProvider(this.dataCacheObject);
    const el = this.render(loadedRoute, dataProvider);

    if (DEV_MODE) {
      render(el, document.body, document.body.firstElementChild!);
    } else {
      hydrate(el, document.body);
    }
  }

  async renderStatic(path: string): Promise<StaticRenderResult | null> {
    const route = this.router.match(path);
    if (!route) {
      return null;
    }

    const dataProvider = new ServerDataProvider(dataSpec);
    const loadedRoute = await loadRoute(route);
    const el = this.render(loadedRoute, dataProvider);

    await ssrPrepass(el);
    const content = DEV_MODE ? renderToString(el) : renderToString(el);

    return {
      routeIndex: route.index,
      content,
      appErrorType: this.lastReportedAppErrorType,
      modules: route.modules,
      dataCacheObject: dataProvider.getCache(),
    };
  }

  private render(loadedRoute: LoadedRoute, dataProvider: DataProvider) {
    const dataContext = {
      provider: dataProvider,
    };

    return (
      <AppContext.Provider value={this.appContext}>
        <DataContext.Provider value={dataContext}>
          <ErrorWrapper>
            <RouterComponent initialRoute={loadedRoute} router={this.router} />
          </ErrorWrapper>
        </DataContext.Provider>
      </AppContext.Provider>
    );
  }
}

interface StaticRenderResult {
  content: string;
  appErrorType: AppErrorType | null;
  routeIndex: number;
  modules: string[];
  dataCacheObject: DataCacheObject;
}

interface AppNavigationResult {
  index: number;
  render: () => VNode;
  renderFinal: () => Promise<VNode>;
  modules: string[];
  params: { [key: string]: unknown };
}
